/**
 * Sincroniza produtos bônus da barra de prêmios com o carrinho:
 * - Ao atingir a meta (quantidade ou valor), adiciona o produto bônus ao carrinho.
 * - Ao ficar abaixo da meta, remove o produto bônus do carrinho.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const SOURCE = 'cart-prize-bonus';
let debounceTimer = null;
let isProcessing = false;

function getConfig() {
  const script = document.querySelector('script[data-cart-prize-bonus-config]');
  if (!script) return null;
  const sectionId = script.dataset.sectionId;
  if (!sectionId) return null;
  try {
    const data = JSON.parse(script.textContent || '{}');
    if (!data.thresholds || !data.variantIds) return null;
    return { sectionId, ...data };
  } catch {
    return null;
  }
}

function getCurrentValue(cart, mode) {
  if (mode === 'quantity') return cart.item_count;
  return cart.total_price; // já em centavos
}

/** No modo total, thresholds no config estão em reais; comparamos com total_price (centavos). */
function getThresholdForCompare(threshold, mode) {
  if (mode === 'total') return Number(threshold) * 100;
  return Number(threshold);
}

async function syncBonusProducts(config, cartFromEvent = null) {
  if (isProcessing) return;
  isProcessing = true;

  try {
    const { sectionId, mode, thresholds, variantIds } = config;
    const cartJsUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_url) ? `${Theme.routes.cart_url}.js` : '/cart.js';
    
    // Usa o cart do evento se disponível, senão busca
    let cart = cartFromEvent;
    if (!cart || !cart.items) {
      const res = await fetch(cartJsUrl);
      if (!res.ok) return;
      cart = await res.json();
    }

    const currentValue = getCurrentValue(cart, mode);
    const bonusVariantIds = variantIds.filter((id) => id != null && id > 0);

    const shouldHave = new Set();
    const n = Math.min(thresholds.length, variantIds.length);
    for (let i = 0; i < n; i++) {
      const threshold = getThresholdForCompare(thresholds[i], mode);
      if (variantIds[i] && currentValue >= threshold) {
        shouldHave.add(variantIds[i]);
      }
    }

    const inCartByVariant = new Map();
    const linesToRemove = [];
    for (const item of cart.items) {
      if (!bonusVariantIds.includes(item.variant_id)) continue;
      inCartByVariant.set(item.variant_id, item);
      if (!shouldHave.has(item.variant_id)) {
        linesToRemove.push({ key: item.key, variantId: item.variant_id });
      }
    }

    const toAdd = [...shouldHave].filter((vid) => !inCartByVariant.has(vid));
    
    // Se não há nada para fazer, sai
    if (linesToRemove.length === 0 && toAdd.length === 0) return;

    const cartAddUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) ? Theme.routes.cart_add_url : '/cart/add.js';
    const cartChangeUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_change_url) ? Theme.routes.cart_change_url : '/cart/change';
    const changeUrl = cartChangeUrl.endsWith('.js') ? cartChangeUrl : `${cartChangeUrl}.js`;

    // Remoções e adições em paralelo
    const operations = [];

    if (linesToRemove.length > 0) {
      operations.push(
        ...linesToRemove.map(({ key }) => {
          const body = new FormData();
          body.append('id', key);
          body.append('quantity', '0');
          return fetch(changeUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
        })
      );
    }

    if (toAdd.length > 0) {
      const items = toAdd.map((id) => ({ id, quantity: 1 }));
      operations.push(
        fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        })
      );
    }

    // Executa todas as operações em paralelo
    await Promise.all(operations);

    // Busca cart final e re-renderiza em paralelo
    const [finalRes, sectionHtml] = await Promise.all([
      fetch(cartJsUrl),
      sectionRenderer.renderSection(sectionId, { cache: false })
    ]);

    const finalCart = finalRes.ok ? await finalRes.json() : cart;

    // Dispara evento único com tudo atualizado
    document.dispatchEvent(
      new CartUpdateEvent({}, sectionId, {
        source: SOURCE,
        itemCount: finalCart.item_count ?? 0,
        sections: sectionHtml ? { [sectionId]: sectionHtml } : undefined,
      })
    );
  } finally {
    isProcessing = false;
  }
}

function handleCartUpdate(event) {
  if (event.detail?.data?.source === SOURCE) return;
  const config = getConfig();
  if (!config) return;

  // Debounce mínimo para atualização o mais instantânea possível
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const cartFromEvent = event.detail?.resource || event.detail?.cart || null;
    syncBonusProducts(config, cartFromEvent).catch(() => {});
  }, 0);
}

document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
